import { Button, ButtonsGroup, Card, Grid, Heading, InputText, PageHeader, PageWrapper, Typo } from "#/atoms";
import { DESKTOP_SPACING, FONT_SIZE, NATURAL, PRIMARY_BLUE } from "@/theme";
import { FC, memo, useCallback } from "react";

import { RadioButtonGroup } from "@/components/molecules";
import { RadioButtonProps } from "@/components/molecules/RadioButtonGroup/RadioButtonGroup.interface";
import { SettingsProps } from "./Settings.interface";
import { useInput } from "@/hooks";

const Settings: FC<SettingsProps> = ({ 
  onSaveSettings,
  onSaveVirtualTourSettings,
  onSaveCancelFeeSettings,
  onSaveInvoiceDueDaysSettings,
  onSaveUpsell,
  settings,
  upsell }) => {
  const upsellAmount = useInput(`${upsell?.defaultPhotos}` || '');
  const upsellPrice = useInput(`${upsell?.price}` || '');
  const floorAreaSurchargeAmount = useInput(`${settings?.floorAreaSurchargeAmount}` || '');
  const floorAreaSurchargeThreshold = useInput(`${settings?.floorAreaSurchargeThreshold}` || '');
  const virtualTourImagesSurchargeAmount = useInput(`${settings?.virtualTourImagesSurchargeAmount}` || '');
  const virtualTourImagesThreshold = useInput(`${settings?.virtualTourImagesThreshold}` || '');
  const cancelFeeAmount = useInput(`${settings?.cancelFeeAmount}` || '');
  const cancelFeeType = useInput(`${settings?.cancelFeeType}` || '');
  const invoiceDueDateDays = useInput(`${settings?.invoiceDueDateDays}` || '');

  const cancelFeeTypes : RadioButtonProps[] = [{
      id: "1",
      label: "Fixed Amount",
      value: "1"
    },
    {
      id: "2",
      label: "Percentage",
      value: "2"
    }
  ]

  const handleUpsellSave = useCallback(() => {
    if (!upsell) {
      return;
    }

    onSaveUpsell({
      defaultPhotos: Number(upsellAmount.value),
      id: upsell?.id,
      price: Number(upsellPrice.value),
    });
  }, [upsell, upsellAmount, upsellPrice]);

  const handleSettingsSave = useCallback(() => {
    if (!settings) {
      return;
    }

    onSaveSettings({
      floorAreaSurchargeAmount: Number(floorAreaSurchargeAmount.value),
      floorAreaSurchargeThreshold: Number(floorAreaSurchargeThreshold.value),
      id: settings?.id,
    });
  }, [settings, floorAreaSurchargeAmount, floorAreaSurchargeThreshold]);

  const handleVirtualTourSettingsSave = useCallback(() => {
    if (!settings) {
      return;
    }

    onSaveVirtualTourSettings({
      id: settings?.id,
      virtualTourImagesSurchargeAmount: Number(virtualTourImagesSurchargeAmount.value),
      virtualTourImagesThreshold: Number(virtualTourImagesThreshold.value),
    });
  }, [settings, virtualTourImagesSurchargeAmount, virtualTourImagesThreshold]);

  const handleCancelFeeTypeChange = useCallback((groupName: string, selectedValue: string) => {
    cancelFeeType.setValue(selectedValue);
  }, []);

  const handleCancelFeeSettingsSave = useCallback(() => {
    if (!settings) {
      return;
    }

    onSaveCancelFeeSettings({
      cancelFeeAmount: Number(cancelFeeAmount.value),
      cancelFeeType: Number(cancelFeeType.value),
      id: settings?.id,
    });
  }, [settings, cancelFeeAmount, cancelFeeType]);

  const handleInvoiceDueDaysSettingsSave = useCallback(() => {
    if (!settings) {
      return;
    }

    onSaveInvoiceDueDaysSettings({
      id: settings?.id,
      invoiceDueDateDays: Number(invoiceDueDateDays.value),
    });
  }, [settings, cancelFeeAmount, cancelFeeType]);

  return (
    <PageWrapper>
      <PageHeader>
        <Heading scale="h1" fontColor={NATURAL.K}>Settings</Heading>
        <Typo fontColor={NATURAL.G} fontSize={FONT_SIZE.SM}>Global system settings</Typo>
      </PageHeader>

      <Card>
        <Heading display="flex" scale="h4" fontColor={PRIMARY_BLUE.DARK}>
          Common Upsell Price Per Photo
        </Heading>
        <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(2, 1fr)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
          <InputText type="number" label="Price (GBP)" value={upsellPrice.value} onChange={upsellPrice.onChange} />
          <InputText type="number" label="Default Photos Amount" value={upsellAmount.value} onChange={upsellAmount.onChange} />
        </Grid>
        <ButtonsGroup fullWidth>
          <Button onClick={handleUpsellSave}>Save</Button>
        </ButtonsGroup>
      </Card>

      <Card>
        <Heading display="flex" scale="h4" fontColor={PRIMARY_BLUE.DARK}>
          Extra Charge per sq ft
        </Heading>
        <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(2, 1fr)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
          <InputText type="number" label="Charge extra for floor plans above this sq ft" value={floorAreaSurchargeThreshold.value} onChange={floorAreaSurchargeThreshold.onChange} />
          <InputText type="number" label="Amount to charge per sq ft (GBP)" value={floorAreaSurchargeAmount.value} onChange={floorAreaSurchargeAmount.onChange} />
        </Grid>
        <ButtonsGroup fullWidth>
          <Button onClick={handleSettingsSave}>Save</Button>
        </ButtonsGroup>
      </Card>

      <Card>
        <Heading display="flex" scale="h4" fontColor={PRIMARY_BLUE.DARK}>
          Extra charge for Virtual Tour
        </Heading>
        <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(2, 1fr)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
          <InputText type="number" label="Charge extra for photos above this number" value={virtualTourImagesThreshold.value} onChange={virtualTourImagesThreshold.onChange} />
          <InputText type="number" label="Amount to charge per extra photos (GBP)" value={virtualTourImagesSurchargeAmount.value} onChange={virtualTourImagesSurchargeAmount.onChange} />
        </Grid>
        <ButtonsGroup fullWidth>
          <Button onClick={handleVirtualTourSettingsSave}>Save</Button>
        </ButtonsGroup>
      </Card>

      <Card>
        <Heading display="flex" scale="h4" fontColor={PRIMARY_BLUE.DARK}>
        Extra charge for cancelled orders
        </Heading>
        <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(2, 1fr)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
          <RadioButtonGroup groupName="cancelFeeType" inputs={cancelFeeTypes} selectedValue={cancelFeeType.value} onChangeValue={handleCancelFeeTypeChange} />
          <InputText type="number" label="Cancellation fee amount" value={cancelFeeAmount.value} onChange={cancelFeeAmount.onChange} />
        </Grid>
        <ButtonsGroup fullWidth>
          <Button onClick={handleCancelFeeSettingsSave}>Save</Button>
        </ButtonsGroup>
      </Card>

      <Card>
        <Heading display="flex" scale="h4" fontColor={PRIMARY_BLUE.DARK}>
        Invoicing time frame
        </Heading>
        <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(1, 1fr)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
          <InputText type="number" label="Days to pay invoices - global override" value={invoiceDueDateDays.value} onChange={invoiceDueDateDays.onChange} />
        </Grid>
        <ButtonsGroup fullWidth>
          <Button onClick={handleInvoiceDueDaysSettingsSave}>Save</Button>
        </ButtonsGroup>
      </Card>
      
    </PageWrapper>
  );
}

export default memo(Settings);